import Image from "next/image";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import DesoAvatar from "@/components/shared/deso-avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { useUserBalancesSignalState } from "@/contexts/user-context";
import {
  DUSD_TICKER_DISPLAY_NAME,
  TOKEN_DISPLAY_NAME,
} from "@/utils/constants";
import { focusToUsd, nanosToInteger } from "@/utils/currency";
import type { Maybe, PartialAccount } from "@/utils/types";
import { getUserName } from "@/utils/usernames";

interface UserRealTimeBalanceProps {
  account: Maybe<PartialAccount>;
  ccBalanceNanos: number;
  loadingCCBalance: boolean;
  showCCBalance?: boolean;
}

const UserRealTimeBalance = ({
  ccBalanceNanos,
  loadingCCBalance,
  account,
  showCCBalance = true,
}: UserRealTimeBalanceProps) => {
  const { focusExchangeRate, loading: loadingExchangeRate } =
    useFocusMarketData();
  const { realTimeFocusUnlockedBalanceNanos } = useUserBalancesSignalState();

  const currentUserCCBalance = nanosToInteger(ccBalanceNanos / 1e9);

  if (loadingExchangeRate || !focusExchangeRate || loadingCCBalance) {
    return (
      <div className="flex flex-row gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-6">
      <div className="mb-0">
        <div className="flex flex-row gap-x-1">
          <div className="flex items-center gap-x-1">
            <Image
              src="/assets/coin-focus.png"
              width={40}
              height={40}
              alt="FOCUS"
              className="mr-1 rounded-full border"
            />
          </div>

          <div className="flex flex-col">
            <span className="font-mono text-sm">
              <CoinPriceFormatter
                price={nanosToInteger(realTimeFocusUnlockedBalanceNanos)}
                className="text-sm"
                isUsd={false}
              />{" "}
              <span className="text-xs text-muted">{TOKEN_DISPLAY_NAME}</span>
            </span>

            <span className="text-xs text-muted">
              ~
              <CoinPriceFormatter
                price={focusToUsd(
                  nanosToInteger(realTimeFocusUnlockedBalanceNanos),
                  focusExchangeRate,
                )}
                highPriceThreshold={1}
              />{" "}
              {DUSD_TICKER_DISPLAY_NAME}
            </span>
          </div>
        </div>
      </div>

      {showCCBalance && (
        <div className="flex items-center gap-x-1">
          {account && (
            <DesoAvatar account={account} size={40} className="mr-1" />
          )}
          <div className="flex flex-col">
            <span className="font-mono text-sm">
              <CoinPriceFormatter
                price={currentUserCCBalance}
                className="text-sm"
                isUsd={false}
              />
            </span>
            <span className="text-xs text-muted">
              {getUserName({
                account,
              })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRealTimeBalance;
